import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingCart, FileText, FlaskConical } from "lucide-react";
import { useCart, tierFor, unitPriceCents } from "@/context/CartContext";
import { toast } from "sonner";
import PageShell from "@/components/PageShell";
import Seo from "@/components/Seo";
import ResponsiveImage from "@/components/ResponsiveImage";
import { useProducts, useCurrentLots } from "@/lib/useProducts";

function fmt(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function SpecRow({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-2.5 border-b border-slate-100 text-sm">
      <span className="font-plexmono text-[10px] tracking-[0.16em] uppercase text-slate-400 pt-0.5">{k}</span>
      <span className="text-slate-700 text-right">{v}</span>
    </div>
  );
}

function productJsonLd(product) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${product.name} — ${product.size}`,
    sku: product.sku,
    brand: { "@type": "Brand", name: "Lab2o" },
    description: product.description,
    image: `https://lab2o.com${product.image}`,
    offers: {
      "@type": "Offer",
      price: (product.base_price_cents / 100).toFixed(2),
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: "https://lab2o.com/bacteriostatic-water",
      itemCondition: "https://schema.org/NewCondition",
    },
  };
}

function ProductDetailCard({ product, lot }) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const unit = unitPriceCents(product.base_price_cents, qty, product.bulk_tiers);
  const tier = tierFor(qty, product.bulk_tiers);
  const isCase = product.sell_unit !== "single";

  const handleAdd = () => {
    addItem({
      id: product.product_id,
      product_id: product.product_id,
      name: product.name,
      size: product.size,
      sku: product.sku,
      image: product.image,
      baseCents: product.base_price_cents,
      tiers: product.bulk_tiers,
      qty,
    });
    toast.success("Added to cart", { description: `${product.name} — ${product.size} × ${qty}` });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="border border-slate-200 bg-white overflow-hidden"
      data-testid={`bw-product-${product.product_id}`}
    >
      <script type="application/ld+json">{JSON.stringify(productJsonLd(product))}</script>
      <div className="grid md:grid-cols-2">
        <div className="bg-slate-50 border-b md:border-b-0 md:border-r border-slate-200 flex items-center justify-center p-8">
          <ResponsiveImage
            src={product.image}
            alt={`${product.name} ${product.size} sterile glass research vial, unlabeled`}
            width={1408}
            height={768}
            className="max-h-64 w-auto object-contain"
            loading="lazy"
            dataTestid={`bw-product-image-${product.product_id}`}
          />
        </div>
        <div className="p-7 sm:p-9">
          <p className="font-plexmono text-[10px] tracking-[0.2em] uppercase text-slate-400">{product.sku}</p>
          <h2 className="mt-2 font-chivo font-extrabold tracking-tight text-2xl text-slate-950" data-testid={`bw-product-name-${product.product_id}`}>
            {product.name} — {product.size}
          </h2>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">{product.description}</p>

          <div className="mt-6">
            <p className="font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-400 mb-2">Specification</p>
            <SpecRow k="Contents" v={isCase ? `Box of 10 vials · ${product.size} each` : `${product.size} vial`} />
            <SpecRow k="Benzyl Alcohol" v="0.9% bacteriostatic preservative" />
            <SpecRow k="Grade" v="Laboratory research reagent" />
            <SpecRow k="Labelling" v="Unlabeled — bare glass" />
          </div>

          <div className="mt-6 border-t border-slate-200 pt-4">
            <div className="flex items-baseline gap-3">
              <span className="font-chivo font-black text-3xl text-slate-950" data-testid={`bw-product-price-${product.product_id}`}>
                {fmt(unit)}
              </span>
              <span className="font-plexmono text-[10px] tracking-[0.15em] uppercase text-slate-400">
                per {isCase ? "box" : "vial"}
              </span>
              {tier && (
                <span className="font-plexmono text-[10px] tracking-[0.15em] uppercase text-emerald-600" data-testid={`bw-product-bulk-${product.product_id}`}>
                  Bulk −{tier.discount_pct}%
                </span>
              )}
            </div>
            {product.bulk_tiers && product.bulk_tiers.length > 0 && (
              <ul className="mt-3 flex flex-col gap-1">
                {product.bulk_tiers.map((t) => (
                  <li key={t.min} className="text-xs text-slate-500" data-testid={`bw-tier-${product.product_id}-${t.min}`}>
                    {t.min}+ {isCase ? "boxes" : "vials"} — {t.discount_pct}% off per unit
                  </li>
                ))}
              </ul>
            )}
          </div>

          {lot && (
            <div className="mt-6 border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`bw-current-lot-${product.product_id}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-400">Current lot</p>
                  <p className="font-plexmono text-sm text-slate-950 mt-0.5" data-testid={`bw-lot-number-${product.product_id}`}>{lot.lot_number}</p>
                  {lot.report_date && <p className="text-[11px] text-slate-500 mt-0.5">Tested {lot.report_date}</p>}
                  {lot.expiration_date && <p className="text-[11px] text-slate-500 mt-0.5">Expiry {lot.expiration_date}</p>}
                </div>
                {lot.coa_pdf_url && (
                  <a
                    href={lot.coa_pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 border border-slate-950 px-3 py-2 font-plexmono text-[10px] tracking-[0.12em] uppercase hover:bg-slate-950 hover:text-white transition-colors shrink-0"
                    data-testid={`bw-view-coa-${product.product_id}`}
                  >
                    <FileText className="w-3.5 h-3.5" /> View COA
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="mt-7 flex items-center gap-4">
            <div className="flex items-center border border-slate-200">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-slate-100" aria-label="Decrease quantity" data-testid={`bw-qty-minus-${product.product_id}`}>
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="w-10 text-center font-plexmono text-sm" data-testid={`bw-qty-${product.product_id}`}>{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-slate-100" aria-label="Increase quantity" data-testid={`bw-qty-plus-${product.product_id}`}>
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            <motion.button
              whileTap={{ scale: 0.98 }}
              onClick={handleAdd}
              className="flex-1 bg-slate-950 text-white py-3.5 font-plexmono text-[11px] tracking-[0.2em] uppercase hover:bg-slate-800 transition-colors flex items-center justify-center gap-3"
              data-testid={`bw-add-to-cart-${product.product_id}`}
            >
              <ShoppingCart className="w-3.5 h-3.5" /> Add to Cart
            </motion.button>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function BacteriostaticWater() {
  const { products, loaded } = useProducts();
  const lotsBySku = useCurrentLots();

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://lab2o.com/" },
      { "@type": "ListItem", position: 2, name: "Bacteriostatic Water", item: "https://lab2o.com/bacteriostatic-water" },
    ],
  };

  return (
    <PageShell
      testid="bacteriostatic-water-page"
      overline={<span className="flex items-center gap-2"><FlaskConical className="w-3.5 h-3.5" /> Laboratory research reagent</span>}
      title="Bacteriostatic Water (0.9% Benzyl Alcohol)"
      intro="Sterile bacteriostatic water containing 0.9% benzyl alcohol, supplied in boxes of unlabeled glass vials. Supplied strictly as a laboratory research reagent. For research purposes only. Not for human or veterinary use or consumption."
    >
      <Seo
        title="Bacteriostatic Water, 0.9% Benzyl Alcohol | Lab2o"
        path="/bacteriostatic-water"
        description="Buy bacteriostatic water vials with 0.9% benzyl alcohol. Lot number and CoA on every batch. Bulk pricing and fast US shipping."
      />
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>

      <div className="mt-12 flex flex-col gap-8">
        {!loaded && (
          <p className="text-center text-slate-400 font-plexmono text-xs tracking-[0.2em] uppercase py-16" data-testid="bw-loading">
            Loading catalogue…
          </p>
        )}
        {loaded && products.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-16" data-testid="bw-unavailable">
            The catalogue is temporarily unavailable. Please email orders@lab2o.com.
          </p>
        )}
        {products.map((p) => (
          <ProductDetailCard key={p.product_id} product={p} lot={lotsBySku[p.sku]} />
        ))}
      </div>
    </PageShell>
  );
}
